import React, { useState, useEffect } from 'react';
import { MessageSquare, MessageCircle, Send, Pencil, XCircle } from 'lucide-react';
import { CONFIG } from '../config';

const ScheduledMessagesPage = ({ customGroups = null }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('hide-cancelled');
  const [dateFilter, setDateFilter] = useState('today-future');
  const [typeFilter, setTypeFilter] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editFormData, setEditFormData] = useState({
    scheduled_date: '',
    scheduled_time: '',
    message: '',
    recipients: [],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    template_vars: {},
  });

  const recipientOptions = ['All', ...(customGroups || ['Bride', 'Groom', 'Bridal Party', 'Event Staff'])];

  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo',
    'Australia/Sydney',
  ];

  useEffect(() => {
    fetchMessages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiStatus = statusFilter === 'hide-cancelled' ? '' : statusFilter;
      const url = apiStatus
        ? `${CONFIG.apiBaseUrl}/scheduled_messages?status=${apiStatus}`
        : `${CONFIG.apiBaseUrl}/scheduled_messages`;

      const resp = await fetch(url, {
        credentials: 'include',
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch messages: ${resp.statusText}`);
      }

      const data = await resp.json();
      const fetched = data.messages || [];
      setMessages(statusFilter === 'hide-cancelled'
        ? fetched.filter(m => m.status !== 'cancelled')
        : fetched
      );
    } catch (err) {
      console.error('Error fetching scheduled messages:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const toLocalTimeString = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const handleEdit = (message) => {
    setEditingMessage(message);

    // Pre-fill date/time in the message's stored timezone so the user sees
    // the time they originally entered, not the browser's local equivalent.
    let localDate = '';
    let localTime = '';
    if (message.scheduled_time) {
      const tz = message.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
      }).formatToParts(new Date(message.scheduled_time));
      const get = (type) => parts.find(p => p.type === type)?.value || '';
      localDate = `${get('year')}-${get('month')}-${get('day')}`;
      localTime = `${get('hour').padStart(2, '0')}:${get('minute')}`;
    }

    setEditFormData({
      scheduled_date: localDate,
      scheduled_time: localTime,
      message: message.message_body || '',
      recipients: message.recipients || [],
      timezone: message.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      template_vars: message.template_vars || {},
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      // Combine date and time, converting from the stored timezone to UTC.
      // Same adjustment used in the send pages: compute the delta between
      // browser-local interpretation and the target timezone, then apply it.
      let scheduledTimeISO = null;
      if (editFormData.scheduled_date && editFormData.scheduled_time) {
        const tz = editFormData.timezone;
        const naive = new Date(`${editFormData.scheduled_date}T${editFormData.scheduled_time}`);
        const inTZ = new Date(naive.toLocaleString('en-US', { timeZone: tz }));
        scheduledTimeISO = new Date(naive.getTime() + (naive.getTime() - inTZ.getTime())).toISOString();
      }

      const messageType = editingMessage.message_type || 'sms';
      const payload = {
        scheduled_time: scheduledTimeISO,
        recipients: editFormData.recipients,
        timezone: editFormData.timezone,
      };

      // Add message content based on type
      if (messageType === 'whatsapp') {
        payload.template_vars = editFormData.template_vars;
      } else {
        payload.message = editFormData.message;
      }

      const resp = await fetch(
        `${CONFIG.apiBaseUrl}/scheduled_messages/${editingMessage.message_id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'include',
        }
      );

      if (!resp.ok) {
        const errorData = await resp.json();
        throw new Error(errorData.error || 'Failed to update message');
      }

      setShowEditModal(false);
      setEditingMessage(null);
      fetchMessages();
    } catch (err) {
      console.error('Error updating message:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleCancel = async (messageId) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled message?')) {
      return;
    }

    try {
      const resp = await fetch(
        `${CONFIG.apiBaseUrl}/scheduled_messages/${messageId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!resp.ok) {
        const errorData = await resp.json();
        throw new Error(errorData.error || 'Failed to cancel message');
      }

      fetchMessages();
    } catch (err) {
      console.error('Error cancelling message:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleSendNow = async (message) => {
    if (!window.confirm('Are you sure you want to send this message immediately?')) {
      return;
    }

    try {
      const now = new Date();
      now.setMinutes(now.getMinutes() + 1);

      const payload = {
        scheduled_time: now.toISOString(),
        timezone: message.timezone,
      };

      const resp = await fetch(
        `${CONFIG.apiBaseUrl}/scheduled_messages/${message.message_id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'include',
        }
      );

      if (!resp.ok) {
        const errorData = await resp.json();
        throw new Error(errorData.error || 'Failed to update message');
      }

      alert('Message rescheduled to send within 1 minute');
      fetchMessages();
    } catch (err) {
      console.error('Error sending message now:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleRecipientChange = (value) => {
    setEditFormData(prev => ({
      ...prev,
      recipients: prev.recipients.includes(value)
        ? prev.recipients.filter(r => r !== value)
        : [...prev.recipients, value],
    }));
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      sent: 'status-sent',
      failed: 'status-failed',
      cancelled: 'status-cancelled',
      partial: 'status-partial',
    };
    return statusClasses[status] || 'status-unknown';
  };

  const formatDateTime = (isoString, tz) => {
    if (!isoString) return 'N/A';
    const timeZone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const date = new Date(isoString);
    const dateStr = date.toLocaleString('en-US', { timeZone, dateStyle: 'medium', timeStyle: 'short' });
    const tzAbbr = date.toLocaleString('en-US', { timeZone, timeZoneName: 'short' }).split(' ').pop();
    return { dateStr, tzAbbr };
  };

  const getMinDate = () => {
    return toLocalDateString(new Date());
  };

  const getMaxDate = () => {
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    return toLocalDateString(oneYearFromNow);
  };

  const filterMessagesByDate = (msgs) => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    return msgs.filter(msg => {
      const scheduledDate = new Date(msg.scheduled_time);

      switch (dateFilter) {
        case 'all':
          return true;
        case 'today':
          return scheduledDate >= startOfToday && scheduledDate <= endOfToday;
        case 'future':
          return scheduledDate > endOfToday;
        case 'past':
          return scheduledDate < startOfToday;
        case 'today-future':
        default:
          return scheduledDate >= startOfToday;
      }
    });
  };

  const filteredMessages = filterMessagesByDate(messages).filter(m =>
    !typeFilter || m.message_type === typeFilter
  );

  return (
    <div className="scheduled-messages-page">
      <h2 className="page-title">Scheduled Messages</h2>

      {loading && <div className="loading-message">Loading scheduled messages...</div>}

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {!loading && !error && (
        <div className="messages-container">
          <div className="page-controls">
            <div className="filter-group">
              <label htmlFor="dateFilter">Filter by date:</label>
              <select
                id="dateFilter"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="date-filter"
              >
                <option value="today-future">Today + Future</option>
                <option value="all">All</option>
                <option value="today">Today Only</option>
                <option value="future">Future Only</option>
                <option value="past">Past</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="statusFilter">Filter by status:</label>
              <select
                id="statusFilter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="status-filter"
              >
                <option value="hide-cancelled">All (excl. Cancelled)</option>
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="typeFilter">Filter by type:</label>
              <select
                id="typeFilter"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="status-filter"
              >
                <option value="">All</option>
                <option value="sms">SMS</option>
                <option value="whatsapp">WhatsApp</option>
              </select>
            </div>

            <button onClick={fetchMessages} className="btn btn-refresh">
              Refresh
            </button>
          </div>

          {filteredMessages.length === 0 && messages.length === 0 && (
            <div className="no-messages">
              No scheduled messages found.
            </div>
          )}

          {filteredMessages.length === 0 && messages.length > 0 && (
            <div className="no-messages">
              No messages match the current filters.
            </div>
          )}

          {filteredMessages.length > 0 && (
            <div className="messages-table-wrapper">
              <table className="messages-table">
                <thead>
                  <tr>
                    <th className="type-column">Type</th>
                    <th>Scheduled Time</th>
                    <th>Recipients</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>Created By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMessages.map((msg) => (
                    <tr key={msg.message_id}>
                      <td className="type-column">
                        {msg.message_type === 'whatsapp' ? (
                          <MessageCircle size={20} color="#25D366" title="WhatsApp" />
                        ) : (
                          <MessageSquare size={20} color="#007acc" title="SMS" />
                        )}
                      </td>
                      <td>
                        {(() => {
                          const { dateStr, tzAbbr } = formatDateTime(msg.scheduled_time, msg.timezone);
                          return (
                            <>
                              {dateStr}
                              <div style={{ fontSize: '0.78em', color: '#6b7280', marginTop: '2px' }}>{tzAbbr}</div>
                            </>
                          );
                        })()}
                      </td>
                      <td>{msg.recipients?.join(', ') || 'N/A'}</td>
                      <td className="message-full">
                        {msg.message_type === 'whatsapp'
                          ? <>
                              {msg.message_body || '[WhatsApp Template]'}
                              {msg.template_name && (
                                <span style={{ color: '#6b7280', fontSize: '0.82em', marginLeft: '6px', whiteSpace: 'nowrap' }}>
                                  ({msg.template_name})
                                </span>
                              )}
                            </>
                          : msg.message_body || 'N/A'}
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusBadgeClass(msg.status)}`}>
                          {msg.status || 'unknown'}
                        </span>
                        {msg.sent_count > 0 && (
                          <div className="sent-count">Sent: {msg.sent_count}</div>
                        )}
                      </td>
                      <td>{msg.created_by || 'N/A'}</td>
                      <td className="actions-cell">
                        {msg.status === 'pending' ? (
                          <div className="action-icons">
                            <button
                              onClick={() => handleEdit(msg)}
                              className="action-icon-btn"
                              title="Edit"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => handleSendNow(msg)}
                              className="action-icon-btn action-icon-send"
                              title="Send Now"
                            >
                              <Send size={16} />
                            </button>
                            <button
                              onClick={() => handleCancel(msg.message_id)}
                              className="action-icon-btn action-icon-cancel"
                              title="Cancel"
                            >
                              <XCircle size={16} />
                            </button>
                          </div>
                        ) : (
                          <span className="no-actions">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showEditModal && editingMessage && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Scheduled Message</h3>

            <div className="form-group">
              <label htmlFor="editDate" className="form-label">Date:</label>
              <input
                type="date"
                id="editDate"
                value={editFormData.scheduled_date}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, scheduled_date: e.target.value })
                }
                min={getMinDate()}
                max={getMaxDate()}
                className="datetime-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="editTime" className="form-label">Time:</label>
              <input
                type="time"
                id="editTime"
                value={editFormData.scheduled_time}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, scheduled_time: e.target.value })
                }
                className="datetime-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="editTimezone" className="form-label">Timezone:</label>
              <select
                id="editTimezone"
                value={editFormData.timezone}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, timezone: e.target.value })
                }
                className="timezone-select"
              >
                {timezones.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Send To:</label>
              <div className="checkbox-group">
                {recipientOptions.map(option => (
                  <label key={option} className="checkbox-label">
                    <input
                      type="checkbox"
                      value={option}
                      checked={editFormData.recipients.includes(option)}
                      onChange={() => handleRecipientChange(option)}
                      className="checkbox-input"
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {editingMessage.message_type === 'whatsapp' ? (
              <>
                <div className="form-group">
                  <label className="form-label">WhatsApp Template:</label>
                  <div style={{
                    padding: '10px',
                    backgroundColor: '#d1fae5',
                    borderRadius: '6px',
                    border: '1px solid #25D366',
                    color: '#065f46',
                    fontWeight: '600'
                  }}>
                    {editingMessage.template_name || 'N/A'}
                  </div>
                </div>

                {editFormData.template_vars && Object.keys(editFormData.template_vars).length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Template Variables:</label>
                    <div className="template-var-inputs">
                      {Object.keys(editFormData.template_vars).map(varName => (
                        <div key={varName} className="template-var-field">
                          <label>{`{{${varName}}}`}</label>
                          <input
                            type="text"
                            value={editFormData.template_vars[varName] || ''}
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                template_vars: { ...editFormData.template_vars, [varName]: e.target.value }
                              })
                            }
                            className="form-input"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="form-group">
                <label htmlFor="editMessage" className="form-label">Message:</label>
                <textarea
                  id="editMessage"
                  value={editFormData.message}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, message: e.target.value })
                  }
                  rows="5"
                  className="message-textarea"
                />
              </div>
            )}

            <div className="modal-actions">
              <button onClick={handleSaveEdit} className="btn btn-primary">
                Save Changes
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduledMessagesPage;
